import {
  createAsyncThunk,
  createSlice,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { apolloClient } from "../../apollo/graphql";
import {
  ADD_WALL_COMMENT,
  CREATE_WALL_POST,
  DELETE_WALL_COMMENT,
  DELETE_WALL_POST,
  EDIT_WALL_COMMENT,
  EDIT_WALL_POST,
  TOGGLE_PIN_WALL_POST,
  TOGGLE_WALL_REACTION,
} from "../../apollo/mutations";
import { GET_WALL_POSTS } from "../../apollo/queries";
import { RejectWithValueType } from "../auth/types/authType";
import {
  WallFeedFilters,
  WallPost,
  WallPostInput,
  WallPostsWithCount,
  WallState,
} from "./types/wallTypes";

function evictWallCache() {
  apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "wallPosts" });
  apolloClient.cache.gc();
}

export const fetchWallPosts = createAsyncThunk<
  WallPostsWithCount & { append?: boolean },
  WallFeedFilters,
  { rejectValue: RejectWithValueType }
>("wall/fetchWallPosts", async (filters, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const { append, ...variables } = filters;
    const response = await apolloClient.query({
      query: GET_WALL_POSTS,
      variables,
      fetchPolicy: "network-only",
    });
    return {
      ...(response.data.wallPosts as WallPostsWithCount),
      append,
    };
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const createWallPost = createAsyncThunk<
  WallPost,
  WallPostInput,
  { rejectValue: RejectWithValueType }
>("wall/createWallPost", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: CREATE_WALL_POST,
      variables: { input },
    });
    evictWallCache();
    return response.data.createWallPost as WallPost;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const editWallPost = createAsyncThunk<
  WallPost,
  WallPostInput,
  { rejectValue: RejectWithValueType }
>("wall/editWallPost", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: EDIT_WALL_POST,
      variables: { input },
    });
    evictWallCache();
    return response.data.editWallPost as WallPost;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const deleteWallPost = createAsyncThunk<
  string,
  string,
  { rejectValue: RejectWithValueType }
>("wall/deleteWallPost", async (id, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    await apolloClient.mutate({
      mutation: DELETE_WALL_POST,
      variables: { id },
    });
    evictWallCache();
    return id;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const togglePinWallPost = createAsyncThunk<
  WallPost,
  string,
  { rejectValue: RejectWithValueType }
>("wall/togglePinWallPost", async (id, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: TOGGLE_PIN_WALL_POST,
      variables: { id },
    });
    evictWallCache();
    return response.data.togglePinWallPost as WallPost;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const addWallComment = createAsyncThunk<
  WallPost,
  { postId: string; message: string },
  { rejectValue: RejectWithValueType }
>("wall/addWallComment", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: ADD_WALL_COMMENT,
      variables: { input },
    });
    return response.data.addWallComment as WallPost;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const editWallComment = createAsyncThunk<
  WallPost,
  { postId: string; commentId: string; message: string },
  { rejectValue: RejectWithValueType }
>("wall/editWallComment", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: EDIT_WALL_COMMENT,
      variables: { input },
    });
    return response.data.editWallComment as WallPost;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const deleteWallComment = createAsyncThunk<
  WallPost,
  { postId: string; commentId: string },
  { rejectValue: RejectWithValueType }
>("wall/deleteWallComment", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: DELETE_WALL_COMMENT,
      variables: input,
    });
    return response.data.deleteWallComment as WallPost;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const toggleWallReaction = createAsyncThunk<
  WallPost,
  { postId: string; type: string },
  { rejectValue: RejectWithValueType }
>("wall/toggleWallReaction", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: TOGGLE_WALL_REACTION,
      variables: { input },
    });
    return response.data.toggleWallReaction as WallPost;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

const initialState: WallState = {
  posts: [],
  totalCount: 0,
  loading: "idle",
  error: null,
  success: null,
};

/** Replace a post in the feed by _id with the mutation result. */
function replacePost(state: WallState, post: WallPost) {
  if (!post?._id) return;
  const idx = state.posts.findIndex((p) => p._id === post._id);
  if (idx !== -1) state.posts[idx] = post;
}

export const wallSlice = createSlice({
  name: "wall",
  initialState,
  reducers: {
    setError: (state, { payload }) => {
      state.error = payload;
    },
    resetError: (state) => {
      state.error = null;
    },
    setSuccess: (state, { payload }) => {
      state.success = payload;
    },
    resetSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWallPosts.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchWallPosts.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.totalCount = payload.totalCount;
      state.posts = payload.append
        ? [...state.posts, ...payload.posts]
        : payload.posts;
    });
    builder.addCase(fetchWallPosts.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(createWallPost.fulfilled, (state, { payload }) => {
      state.posts.unshift(payload);
      state.totalCount += 1;
      state.success = { message: "Posted" };
    });
    builder.addCase(editWallPost.fulfilled, (state, { payload }) => {
      replacePost(state, payload);
    });
    builder.addCase(deleteWallPost.fulfilled, (state, { payload }) => {
      state.posts = state.posts.filter((p) => p._id !== payload);
      if (state.totalCount > 0) state.totalCount -= 1;
    });
    builder.addCase(togglePinWallPost.fulfilled, (state, { payload }) => {
      replacePost(state, payload);
      // keep pinned posts first, otherwise newest first
      state.posts.sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        return (
          new Date(b.createdAt ?? 0).getTime() -
          new Date(a.createdAt ?? 0).getTime()
        );
      });
    });
    builder.addCase(addWallComment.fulfilled, (state, { payload }) =>
      replacePost(state, payload),
    );
    builder.addCase(editWallComment.fulfilled, (state, { payload }) =>
      replacePost(state, payload),
    );
    builder.addCase(deleteWallComment.fulfilled, (state, { payload }) =>
      replacePost(state, payload),
    );
    builder.addCase(toggleWallReaction.fulfilled, (state, { payload }) =>
      replacePost(state, payload),
    );
  },
});

export const { setError, resetError, setSuccess, resetSuccess } =
  wallSlice.actions;

export const selectWall = (state: RootState) => state.wall as WallState;

async function setErrorAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: any,
) {
  dispatch(setError(payload));
  setTimeout(() => {
    dispatch(resetError());
  }, 6000);
}

export default wallSlice.reducer;
